import { useCallback, useState } from 'react';
import { supabase } from '../supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

export type CreateOrgState = 'idle' | 'creating' | 'error';

export type CreateOrgError =
  | { code: 'invalid_name'; message: string }
  | { code: 'not_authenticated'; message: string }
  | { code: 'unknown'; message: string };

export type CreateOrgOutcome =
  | { ok: true; org_id: string; name: string }
  | { ok: false; error: CreateOrgError };

// ─── Shared validator (exported so OrgCreateForm can reuse without duplication) ─

const RESERVED_NAMES = new Set(['penrose', 'admin', 'system', 'support']);
const FORMAT_RE = /^[A-Za-z0-9][A-Za-z0-9 -]*[A-Za-z0-9]$/;

/**
 * Client-side mirror of the server-side create_org validation.
 * Checks in the same order the RPC does:
 *   1. empty / whitespace
 *   2. trimmed length 2–40
 *   3. format regex
 *   4. reserved names
 * Returns a client-side `invalid_name` error or null if valid.
 */
export function validateOrgName(raw: string): CreateOrgError | null {
  const trimmed = raw.trim();

  if (trimmed.length === 0) {
    return { code: 'invalid_name', message: 'Org name is required.' };
  }

  if (trimmed.length < 2 || trimmed.length > 40) {
    return { code: 'invalid_name', message: 'Org name must be 2 to 40 characters.' };
  }

  if (!FORMAT_RE.test(trimmed)) {
    return {
      code: 'invalid_name',
      message:
        'Org name may contain letters, numbers, spaces, and hyphens, and must start and end with a letter or number.'
    };
  }

  if (RESERVED_NAMES.has(trimmed.toLowerCase())) {
    return { code: 'invalid_name', message: 'That org name is reserved.' };
  }

  return null;
}

// ─── VM ──────────────────────────────────────────────────────────────────────

export interface CreateOrgVM {
  state: CreateOrgState;
  lastError: CreateOrgError | null;
  submit: (rawName: string) => Promise<CreateOrgOutcome>;
  reset: () => void;
}

/**
 * View-model for the "Create new org" form. Owns validation, RPC call,
 * state transitions, and error mapping.
 *
 * Pattern mirrors useOrgSwitcherVM: imports the supabase singleton directly;
 * Playwright specs intercept via page.route on the wire.
 */
export function useCreateOrgVM(): CreateOrgVM {
  const [state, setState] = useState<CreateOrgState>('idle');
  const [lastError, setLastError] = useState<CreateOrgError | null>(null);

  const reset = useCallback(() => {
    setState('idle');
    setLastError(null);
  }, []);

  const submit = useCallback(async (rawName: string): Promise<CreateOrgOutcome> => {
    // 1. Client-side validation — same rules as server, instant feedback.
    const clientError = validateOrgName(rawName);
    if (clientError) {
      setState('error');
      setLastError(clientError);
      return { ok: false, error: clientError };
    }

    const trimmed = rawName.trim();

    // 2. Call the RPC.
    setState('creating');
    setLastError(null);

    try {
      const { data, error } = await supabase.rpc('create_org', {
        p_name: trimmed
      });

      if (error) {
        // Map by error.code (SQLSTATE string), NOT by message text.
        let mapped: CreateOrgError;
        if (error.code === '28000') {
          mapped = {
            code: 'not_authenticated',
            message: error.message || 'Not signed in.'
          };
        } else if (error.code === '22023') {
          mapped = {
            code: 'invalid_name',
            message: error.message || 'Invalid org name.'
          };
        } else {
          mapped = {
            code: 'unknown',
            message: error.message || 'Something went wrong.'
          };
        }
        setState('error');
        setLastError(mapped);
        return { ok: false, error: mapped };
      }

      // Validate success payload shape.
      const payload = data as { org_id?: string; name?: string } | null;
      if (!payload?.org_id || !payload?.name) {
        const unknown: CreateOrgError = {
          code: 'unknown',
          message: 'Empty response from server.'
        };
        setState('error');
        setLastError(unknown);
        return { ok: false, error: unknown };
      }

      setState('idle');
      setLastError(null);
      return { ok: true, org_id: payload.org_id, name: payload.name };
    } catch (err) {
      const unknown: CreateOrgError = {
        code: 'unknown',
        message: err instanceof Error ? err.message : 'Something went wrong.'
      };
      setState('error');
      setLastError(unknown);
      return { ok: false, error: unknown };
    }
  }, []);

  return { state, lastError, submit, reset };
}
